import * as Popover from '@radix-ui/react-popover';
import { addDays, format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import type { ClassNames, Matcher } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';

import { ChevronIcon } from '../ChevronIcon/ChevronIcon';
import { dayPickerClassNames } from './DatePicker.styles';

const TODAY = new Date();

TODAY.setHours(0, 0, 0, 0);

const FUTURE_LIMIT = addDays(TODAY, 365 * 10); // 10 years

export interface DatePickerProps {
  value?: Date;
  onApply: (date: Date) => void;
  minDate?: Date;
  allowFutureDates?: boolean;
  placeholder?: string;
  highlightToday?: boolean;
}

export function DatePicker({
  onApply,
  value,
  minDate,
  allowFutureDates = true,
  placeholder = 'Select Date',
  highlightToday = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const handleSelectDate = useCallback(
    (date: Date) => {
      onApply(date);
      setOpen(false);
    },
    [onApply],
  );

  const displayValue = value ? format(value, 'MMM d, yyyy') : placeholder;

  const disabledDates = useMemo(() => {
    const matchers: Matcher[] = [];

    if (minDate) {
      matchers.push({ before: minDate });
    }

    if (!allowFutureDates) {
      matchers.push({ after: TODAY });
    }

    return matchers;
  }, [minDate, allowFutureDates]);

  const classNames: Partial<ClassNames> = { ...dayPickerClassNames };

  if (highlightToday) {
    classNames.today = '[&>button]:underline [&>button]:underline-offset-4';
  }

  const chevronDirection = open ? 'up' : 'down';

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          data-testid="date-picker-trigger"
          className="relative flex items-center gap-sm rounded-sm border py-sm pl-md pr-sm transition-colors duration-100 bg-emphasis-default border-emphasis-subtle text-inverse hover:bg-emphasis-strong"
        >
          <span className="text-white">{displayValue}</span>
          <ChevronIcon direction={chevronDirection} />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          className="z-10 mt-xs w-[18rem] rounded-sm border bg-level-03 border-emphasis-subtle"
        >
          <div className="flex">
            <DayPicker
              showOutsideDays
              required={true}
              mode="single"
              captionLayout="dropdown"
              disabled={disabledDates}
              startMonth={minDate || TODAY}
              endMonth={allowFutureDates ? FUTURE_LIMIT : TODAY}
              selected={value}
              onSelect={handleSelectDate}
              numberOfMonths={1}
              classNames={classNames}
              formatters={{
                formatWeekdayName: (weekday) => format(weekday, 'ccccc'),
                formatMonthDropdown: (month) => format(month, 'MMMM'),
              }}
              data-testid="date-picker-calendar"
            />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

DatePicker.displayName = 'DatePicker';
